import {
  useQuery,
  useMutation,
  useQueryClient,
  UseMutationOptions,
  UseQueryOptions,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from 'react-query'
import { CustomersApi } from '@trybeapp/sdk.customer'
import { apiConfiguration } from 'utilities/TrybeApiClient'
import {
  ActionCustomerSearchRequest,
  Customer,
  CustomersApi as CustomersApiTs,
  CustomerSearchResponse,
  DeleteAssociatedBarcodeRequest,
  ListAssociatedBarcodes,
  ListCustomers,
  ResponseError,
  ShowAssociatedBarcode,
  ShowDiscountType,
  UpdateAssociatedBarcodeRequest,
} from '@trybeapp/sdk'
import { useCurrentSite, useCurrentSiteId } from 'contexts/SiteConfig'
import {
  ApiResponse,
  InfinitePaginatedResponse,
  LegacySdkResponseError,
  PaginatedApiResponse,
} from 'api/api.types'
import { SetOptional } from 'type-fest'

const api = new CustomersApi()
const tsApi = new CustomersApiTs(apiConfiguration)

export const useCustomers = (
  params = {},
  options: UseQueryOptions<ListCustomers, ResponseError> = {}
) =>
  useQuery<ListCustomers, ResponseError>(
    ['customers', { params }],
    () => tsApi.listCustomers(params),
    options
  )

export const useCustomerSearch = () => {
  const { brand_id: brandId } = useCurrentSite()

  return useMutation<ListCustomers, ResponseError, string>((query) => {
    return tsApi.listCustomers({
      query,
      brandId,
    })
  })
}

export const useInfiniteCustomers = (
  params: Record<string, any> = {},
  options: UseInfiniteQueryOptions<InfinitePaginatedResponse<Customer>, LegacySdkResponseError> = {}
) => {
  const siteId = useCurrentSiteId()
  params.siteId = params.siteId ?? siteId
  options.cacheTime = options.cacheTime ?? Infinity
  options.staleTime = options.staleTime ?? 60 * 60 * 1000 // 60 minutes

  return useInfiniteQuery<InfinitePaginatedResponse<Customer>, LegacySdkResponseError>(
    ['infiniteCustomers', { params }],
    async ({ pageParam: page = 1 }) => {
      const response: PaginatedApiResponse<Customer> = await api.listCustomers({ page, ...params })

      return {
        data: response.data.map((customer) => ({
          ...customer,
          name: customer.full_name,
          description: customer.email,
          altText: `${customer.full_name} ${customer.email}`,
        })),
        page,
        nextPage: response.meta?.last_page > page ? page + 1 : undefined,
        total: response.meta?.total,
      }
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextPage,
      ...options,
    }
  )
}

export const useExportCustomers = () => {
  const siteId = useCurrentSiteId()

  return useMutation<ApiResponse<any>, LegacySdkResponseError, Record<string, any>>(
    ['Export'],
    (params) => {
      params.siteId = siteId

      return api.queueListCustomers(params)
    }
  )
}

export const useGetCustomer = (customerId: string, options = {}) =>
  useQuery(['customers', customerId], () => api.getCustomer(customerId), options)

export const useUpdateCustomer = (
  options: UseMutationOptions<any, LegacySdkResponseError, [string, Record<string, any>]> = {}
) => {
  const queryClient = useQueryClient()

  return useMutation<any, LegacySdkResponseError, [string, Record<string, any>]>(
    ([customerId, customer]) => api.updateCustomer(customerId, customer),
    {
      ...options,
      onSuccess: (...args) => {
        const [, [customerId]] = args

        queryClient.invalidateQueries('customers')
        queryClient.invalidateQueries(['auditLogs', { subjectId: customerId }])

        if (options.onSuccess) {
          options.onSuccess(...args)
        }
      },
    }
  )
}

export const useCreateCustomer = (siteId: string) => {
  const queryClient = useQueryClient()

  return useMutation((customer: any) =>
    api.createCustomer(
      {
        createCustomerRequest: {
          ...customer,
          site_id: siteId,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['customers'])
        },
      }
    )
  )
}

export const useDeleteCustomer = (customerId: string) => {
  const queryClient = useQueryClient()

  return useMutation<any, LegacySdkResponseError, null>(() => api.deleteCustomer(customerId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['customers', customerId])
    },
  })
}

export const useResetCustomerPassword = (customerId: string) =>
  useMutation<any, LegacySdkResponseError, null | void>(() => api.resetCustomerPassword(customerId))

export const useTriggerBilling = (customerId: string) =>
  useMutation(() => api.triggerCustomerBilling(customerId))

export const useResendEmailVerification = (customerId: string) =>
  useMutation<any, LegacySdkResponseError, null | void>(() =>
    api.resendEmailVerification(customerId)
  )

export const useGetCustomerAddresses = (customerId: string, options: UseQueryOptions = {}) => {
  // With these defaults, data will always be refreshed
  // in the background if the cache is older than 1 hour.
  options.cacheTime = options.cacheTime ?? Infinity
  options.staleTime = options.staleTime ?? 60 * 60 * 1000 // 60 minutes

  return useQuery<any, LegacySdkResponseError>(
    ['customerAddresses', { customerId }],
    () => api.listCustomerAddresses(customerId),
    options
  )
}

export const useCreateNewCustomerAddress = (customerId: string) => {
  const queryClient = useQueryClient()

  return useMutation(
    (values: any) =>
      api.createCustomerAddress(customerId, {
        createCustomerAddressRequest: values,
      }),
    {
      onSuccess: () => queryClient.invalidateQueries(['customerAddresses']),
    }
  )
}

export const useDeleteCustomerAddress = (customerId: string) => {
  const queryClient = useQueryClient()

  return useMutation<any, LegacySdkResponseError, string>(
    (addressId) => api.deleteCustomerAddress(customerId, addressId),
    {
      onSuccess: () => queryClient.invalidateQueries(['customerAddresses']),
    }
  )
}

export const useUpdateCustomerAddress = (customerId: string) => {
  const queryClient = useQueryClient()

  return useMutation(
    ([addressId, vals]: [string, any]) =>
      api.updateCustomerAddress(customerId, addressId, {
        updateCustomerAddressRequest: vals,
      }),
    {
      onSuccess: () => queryClient.invalidateQueries(['customerAddresses', { customerId }]),
    }
  )
}

export const useBarcodeSearch = (code: string, options: UseQueryOptions = {}) => {
  const siteId = useCurrentSiteId()

  return useQuery<any, any, any>(
    ['getCustomerByBarcode', { code, siteId }],
    () => api.getCustomerByBarcode(code, siteId),
    {
      ...options,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  )
}

export const useAssociatedBarcodesForClient = (
  clientId: string,
  options: UseQueryOptions<ListAssociatedBarcodes, ResponseError> = {}
) =>
  useQuery<ListAssociatedBarcodes, ResponseError>(
    ['listAssociatedBarcodes', { clientId }],
    () =>
      tsApi.listAssociatedBarcodes({
        customerId: clientId,
      }),
    {
      ...options,
      refetchOnWindowFocus: false,
    }
  )

export const useAssociatedBarcodeForClient = (
  clientId: string,
  barcodeId: string,
  options: UseQueryOptions = {}
) =>
  useQuery<any, any, any>(
    ['associatedBarcode', { clientId, barcodeId }],
    () => api.getAssociatedBarcode(clientId, barcodeId),
    {
      ...options,
      refetchOnWindowFocus: false,
    }
  )

export const useAssociateBarcode = () => {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<any>, LegacySdkResponseError, [string, string]>(
    ([clientId, barcode]) =>
      api.associateBarcode(clientId, {
        associateBarcodeRequest: {
          barcode,
        },
      }),
    {
      onSuccess: (_, [clientId]) => {
        queryClient.invalidateQueries(['listAssociatedBarcodes', { clientId }])
        queryClient.invalidateQueries(['associatedBarcode', { clientId }])
      },
    }
  )
}

export const useDisassociateBarcode = () => {
  const queryClient = useQueryClient()

  return useMutation<void, ResponseError, DeleteAssociatedBarcodeRequest>(
    (request) => tsApi.deleteAssociatedBarcode(request),
    {
      onSuccess: (_, request) => {
        queryClient.invalidateQueries(['listAssociatedBarcodes', { clientId: request.customerId }])
        queryClient.invalidateQueries(['associatedBarcode', { clientId: request.customerId }])
      },
    }
  )
}

export const useUpdateAssociatedBarcode = () => {
  const queryClient = useQueryClient()

  return useMutation<ShowAssociatedBarcode, ResponseError, UpdateAssociatedBarcodeRequest>(
    ({ customerId, barcodeId, updateAssociatedBarcode }) =>
      tsApi.updateAssociatedBarcode({ customerId, barcodeId, updateAssociatedBarcode }),
    {
      onSuccess: (_, { customerId }) => {
        queryClient.invalidateQueries(['listAssociatedBarcodes', { clientId: customerId }])
        queryClient.invalidateQueries(['wearables'])
      },
    }
  )
}

export const useLockCustomer = () => {
  const queryClient = useQueryClient()

  return useMutation((customerId: string) => api.lockCustomer(customerId), {
    onSuccess: (_, customerId) => queryClient.invalidateQueries(['customers', customerId]),
  })
}

export const useUnlockCustomer = () => {
  const queryClient = useQueryClient()

  return useMutation((customerId: string) => api.unlockCustomer(customerId), {
    onSuccess: (_, customerId) => queryClient.invalidateQueries(['customers', customerId]),
  })
}

export const useGetCustomerStats = (
  customerId: string,
  options: UseQueryOptions<ShowDiscountType, ResponseError> = {}
) => {
  options.staleTime = options.staleTime ?? 60 * 1000

  return useQuery<ShowDiscountType, ResponseError>(
    ['customerStats', customerId],
    () => tsApi.getCustomerStats({ customerId }),
    options
  )
}

export const useShopCustomerSearch = (
  params: SetOptional<ActionCustomerSearchRequest, 'siteId'>,
  options = {}
) => {
  params.siteId = useCurrentSiteId()

  return useQuery<CustomerSearchResponse, ResponseError>(
    ['customerSearch', { params }],
    () => tsApi.actionCustomerSearch(params as ActionCustomerSearchRequest),
    options
  )
}
